import React from 'react'
import clsx from 'clsx'
import { useFormField } from '@hooks/useFormField'
import { type BaseFormFieldProps } from './TextInput'

export type TextareaProps = BaseFormFieldProps & {
  rows?: number
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(props, ref) {
  const {
    name,
    label,
    required,
    errors,
    dataCompId,
    rows = 3,
    ...textareaProps
  } = props

  const { fieldError, errorId, ariaDescribedBy, requiredText, ariaInvalid } = useFormField(
    name, 
    errors, 
    required
  )

  return (
    <div
      className="tw-flex tw-flex-col tw-justify-center tw-min-w-[0] tw-w-full"
      {...(dataCompId && { 'data-compid': dataCompId })}
    >
      <label
        htmlFor={name}
        className={clsx(
          'tw-mb-0 tw-pb-2 tw-text-gray-700 tw-font-medium',
          fieldError && 'tw-text-red-500'
        )}
      >
        {label} {requiredText}
      </label>

      <textarea
        id={name}
        name={name}
        required={required}
        rows={rows}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        style={{
          resize: "vertical",
        }}
        className={clsx(
          'resizeable tw-font-normal tw-text-base tw-px-3 tw-py-2 tw-w-full',
          'tw-bg-white tw-border tw-rounded tw-border-gray-400',
          'focus:tw-outline-none focus:tw-border-primary-400 focus-within:tw-ring-4 tw-ring-primary-100 focus:tw-ring-offset-0',
          'placeholder:tw-text-gray-500 tw-min-h-[80px]',
          fieldError && 'tw-border-red-400 focus:tw-border-red-500 focus:tw-ring-red-200',
          textareaProps.disabled && 'tw-bg-gray-100 tw-cursor-not-allowed tw-opacity-60'
        )}
        ref={ref}
        {...textareaProps}
      />

      <div role="status" aria-relevant="all">
        {fieldError && (
          <p id={errorId} className="tw-text-red-500 tw-font-light tw-text-sm tw-pt-1">
            {fieldError.message}
          </p>
        )}
      </div>
    </div>
  )
})

export { Textarea }
